import { FC, useState, createContext, useContext } from "react";
import { useApolloClient } from "react-apollo-hooks";
import {
  GetAllUserSubscriptionById,
  GetAllUserSubscriptionByIdVariables,
} from "graphql/types/GetAllUserSubscriptionById";
import { GET_USER_SUBSCRIPTION_DETAILS_BY_ID } from "graphql/subscriptions";
import { MEMBERSHIP_PLAN_MAPPING } from "helpers/subscription_constants";
import {
  ACTIVE_STATUS,
  CIRCLE,
  FAMILY,
  DEFERRED_ACTICE,
} from "helpers/constants";
import { returnUserReadableDate } from "helpers/dateHelpers";

interface SubscriptionMembershipContextProps {
  membershipDetails: any;
  userSavings: any;
  subscriptionDetailsLoading: boolean;
  redirection: boolean;
  getMembershipsDetails: ((planId: string) => void) | null;
}

export const SubscriptionMembershipContext = createContext<SubscriptionMembershipContextProps>(
  {
    membershipDetails: null,
    userSavings: null,
    subscriptionDetailsLoading: false,
    redirection: false,
    getMembershipsDetails: (planId: string) => {},
  }
);

export const SubscriptionMembershipProvider: FC<SubscriptionMembershipContextProps> = (
  props
) => {
  const apolloClient = useApolloClient();
  const [membershipDetails, setMembershipDetails] = useState({
    apolloSubscriptions: null,
    userSubscriptionId: null,
    benefits: [],
    faqs: [],
    termsAndConditions: [],
    bannerInfo: null,
    expiryDate: null,
    remainingDaysToExpire: null,
    isCircle: false,
    logoURL: null,
    renew: false,
    active: false,
    isUpdated: false,
    groupPlan: null,
  });

  const [
    subscriptionDetailsLoading,
    setSubscriptionDetailsLoading,
  ] = useState<boolean>(true);

  const [redirection, setRedirection] = useState<boolean>(false);

  const [userSavings, setUserSavings] = useState({
    pharmaSavings: 0,
    consultSavings: 0,
    deliverySavings: 0,
    totalSavings: 0,
    benefits: [],
  });

  const getMembershipsDetails = (planId: string) => {
    apolloClient
      .query<GetAllUserSubscriptionById, GetAllUserSubscriptionByIdVariables>({
        query: GET_USER_SUBSCRIPTION_DETAILS_BY_ID,
        variables: {
          plan_id: MEMBERSHIP_PLAN_MAPPING[planId.toString()].planId,
          mobile_number: localStorage.getItem("mobile_num"),
        },
      })
      .then(({ data }) => {
        if (data?.GetAllUserSubscriptionsById?.response) {
          let apolloSub = data?.GetAllUserSubscriptionsById?.response;
          let metaData = apolloSub?.group_plan?.meta;
          let apolloSavings = apolloSub?.savings?.savings;
          let membershipDetails = {
            apolloSubscriptions: apolloSub,
            userSubscriptionId: apolloSub?._id,
            benefits: apolloSub?.benefits,
            faqs: metaData?.landing_page_meta_data?.FAQ,
            termsAndConditions: metaData?.landing_page_meta_data?.TnC || [],
            bannerInfo: metaData?.landing_page_meta_data?.banner_info,
            expiryDate: returnUserReadableDate(apolloSub?.end_date),
            remainingDaysToExpire: apolloSub?.expires_in,
            isCircle: planId == CIRCLE,
            isFamilyPlan: planId == FAMILY,
            logoURL: metaData?.landing_page_meta_data?.group_plan_logo_url,
            renew: apolloSub?.renewNow,
            active: apolloSub.status == ACTIVE_STATUS,
            isUpdated: apolloSub.status == DEFERRED_ACTICE,
            groupPlan: metaData?.group_plan?.name,
          };
          setMembershipDetails(membershipDetails);
          let userSavings = {
            benefits: apolloSub?.benefits,
            pharmaSavings: apolloSavings?.pharma,
            consultSavings: apolloSavings?.consult,
            deliverySavings: apolloSavings?.delivery,
            totalSavings: apolloSub?.savings?.totalSavings,
          };
          setUserSavings(userSavings);
          setSubscriptionDetailsLoading(false);
        } else {
          setRedirection(true);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <SubscriptionMembershipContext.Provider
      value={{
        membershipDetails,
        subscriptionDetailsLoading,
        userSavings,
        redirection,
        getMembershipsDetails,
      }}
    >
      {props.children}
    </SubscriptionMembershipContext.Provider>
  );
};

const useSubscriptionMembershipContext = () =>
  useContext<SubscriptionMembershipContextProps>(SubscriptionMembershipContext);

export const useSubscriptionMembershipDetails = () => {
  const context = useSubscriptionMembershipContext();
  return context;
};
